import React, { useEffect, useState } from "react";
import { MapPin, ExternalLink } from "lucide-react";

const geoCache = new Map();

async function geocode(postcode) {
  const pc = (postcode || "").trim();
  if (!pc) return null;
  if (geoCache.has(pc)) return geoCache.get(pc);
  try {
    const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`);
    const json = await res.json();
    const r = json && json.result;
    const coords = r ? { lat: r.latitude, lon: r.longitude } : null;
    geoCache.set(pc, coords);
    return coords;
  } catch {
    geoCache.set(pc, null);
    return null;
  }
}

export function VenueMap({ venue, className = "" }) {
  const serverCoords = (venue && venue.lat != null && venue.lon != null) ? { lat: venue.lat, lon: venue.lon } : null;
  const initial = serverCoords || geoCache.get((venue?.postcode || "").trim());
  const [coords, setCoords] = useState(initial || null);
  const [loading, setLoading] = useState(!initial && !!venue?.postcode);

  useEffect(() => {
    let active = true;
    if (!coords && venue?.postcode) {
      setLoading(true);
      geocode(venue.postcode).then((c) => { if (active) { setCoords(c); setLoading(false); } });
    } else {
      setLoading(false);
    }
    return () => { active = false; };
  }, [venue?.postcode]);

  const query = encodeURIComponent([venue?.name, venue?.address, venue?.town, venue?.postcode].filter(Boolean).join(", "));
  const gmaps = `https://www.google.com/maps/search/?api=1&query=${query}`;
  const openDirections = (e) => { e.preventDefault(); e.stopPropagation(); window.open(gmaps, "_blank", "noopener"); };

  if (coords) {
    const { lat, lon } = coords;
    const d = 0.008;
    const bbox = `${lon - d},${lat - d / 2},${lon + d},${lat + d / 2}`;
    const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lon}`;
    return (
      <div className={`overflow-hidden rounded-xl border border-border ${className}`} data-testid="venue-map">
        <iframe title={`Map showing ${venue?.name || "venue"}`} className="block h-36 w-full" loading="lazy" src={src} />
        <button type="button" onClick={openDirections} data-testid="venue-directions"
          className="flex w-full items-center justify-center gap-1.5 bg-secondary py-1.5 text-xs font-semibold text-[#5C4A42] hover:text-[#B04B26]">
          <ExternalLink size={12} /> Directions
        </button>
      </div>
    );
  }

  return (
    <button type="button" onClick={openDirections} data-testid="venue-map-link"
      className={`flex w-full items-center justify-center gap-1.5 rounded-xl border border-border bg-secondary py-2.5 text-xs font-semibold text-[#5C4A42] hover:text-[#B04B26] ${className}`}>
      <MapPin size={13} /> {loading ? "Loading map…" : "View location on map"}
    </button>
  );
}
